import type { JSX } from 'react';
import { deltaClass, deltaText } from './utils.js';

export interface KpiSet {
  total: number;
  totalDelta: number;
  topsTotal: number;
  bottomsTotal: number;
  topsDelta: number;
  bottomsDelta: number;
  warnCount: number;
  categoryCount: number;
  sizeCount: number;
  sizeRangeLabel: string;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 4つの KPI カード（合計型数 + tops/bottoms breakdown / 対象カテゴリ / 対象サイズ / 閾値割れ）
 * @graph-connects deltaClass [calls] 増減記号
 * @graph-connects deltaText [calls] 増減ラベル
 */
export function StatsGrid(props: { kpis: KpiSet }): JSX.Element {
  const k = props.kpis;
  const max = Math.max(k.topsTotal, k.bottomsTotal, 1);
  const totalPrev = k.total - k.totalDelta;
  const pct = totalPrev > 0 ? (k.totalDelta / totalPrev) * 100 : 0;
  const topsPct = k.total ? Math.round((k.topsTotal / k.total) * 100) : 0;
  const bottomsPct = k.total ? Math.round((k.bottomsTotal / k.total) * 100) : 0;
  return (
    <div className="stats">
      <div className="stat stat-wide">
        <div className="stat-label">合計 選定可能型数</div>
        <div className="stat-value">
          {k.total.toLocaleString()}
          <small>型</small>
        </div>
        <div className={`stat-delta ${deltaClass(k.totalDelta)}`}>
          {deltaText(k.totalDelta)} (前回比 {pct >= 0 ? '+' : ''}
          {pct.toFixed(1)}%)
        </div>
        <div className="stat-breakdown">
          <div className="bd-item">
            <div className="bd-head">
              <span className="bd-tag bd-tops"></span>
              <span className="bd-name">トップス</span>
              <span className="bd-sub">カットソー / シャツ・ブラウス / ニット / ワンピース</span>
            </div>
            <div className="bd-value">
              {k.topsTotal.toLocaleString()}
              <small>型</small>{' '}
              <span className="bd-pct">
                {topsPct}% / 前回比 {deltaText(k.topsDelta)}
              </span>
            </div>
            <div className="bd-bar">
              <div
                className="bd-bar-fill bd-tops-fill"
                style={{ width: `${String((k.topsTotal / max) * 100)}%` }}
              ></div>
            </div>
          </div>
          <div className="bd-item">
            <div className="bd-head">
              <span className="bd-tag bd-bottoms"></span>
              <span className="bd-name">ボトムス</span>
              <span className="bd-sub">スカート / パンツ</span>
            </div>
            <div className="bd-value">
              {k.bottomsTotal.toLocaleString()}
              <small>型</small>{' '}
              <span className="bd-pct">
                {bottomsPct}% / 前回比 {deltaText(k.bottomsDelta)}
              </span>
            </div>
            <div className="bd-bar">
              <div
                className="bd-bar-fill bd-bottoms-fill"
                style={{ width: `${String((k.bottomsTotal / max) * 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div className="stat">
        <div className="stat-label">対象カテゴリ</div>
        <div className="stat-value">
          {k.categoryCount.toLocaleString()}
          <small>カテゴリ</small>
        </div>
        <div className="stat-delta flat">変更なし</div>
      </div>
      <div className="stat">
        <div className="stat-label">対象サイズ</div>
        <div className="stat-value">
          {k.sizeCount.toLocaleString()}
          <small>{k.sizeRangeLabel ? `サイズ (${k.sizeRangeLabel})` : 'サイズ'}</small>
        </div>
        <div className="stat-delta flat">変更なし</div>
      </div>
      <div className="stat has-warning">
        <div className="stat-label">閾値割れ (size×taste)</div>
        <div className="stat-value">
          {k.warnCount.toLocaleString()}
          <small>件</small>
        </div>
      </div>
    </div>
  );
}
